import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { addToConfig } from './utils';
import { selectConfigFlowPages } from '../../../selectors';
import { productOffersCropOptions } from '../products/productConfigPredicates';
import CropOptionsPrepaySelector from '../products/regular/subcomponents/CropOptionsPrepaySelector';
import { MOBILE_L } from '../hooks/screen';

const CropOptionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  max-width: 600px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: ${MOBILE_L}px) {
    padding: 15px;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
  width: 100%;
  justify-content: center;

  @media (max-width: ${MOBILE_L}px) {
    flex-direction: column;
  }
`;

const ActionButton = styled.button`
  border: none;
  background: #479de3;
  color: white;
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 4px;
  cursor: pointer;
  min-width: 120px;
  transition: background-color 0.2s ease;

  &:hover {
    background: #3885d3;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }

  @media (max-width: ${MOBILE_L}px) {
    width: 100%;
    padding: 14px 24px;
  }
`;

const CropOptions = ({ location, history }) => {
  const dispatch = useDispatch();
  const shoot = useSelector((state) => state.shoot);
  const productConfig = useSelector((state) => state.v3Order.productConfig);
  const additionalData = useSelector((state) => state.additionalData);
  const pages = useSelector(selectConfigFlowPages);

  const cropOptions = shoot?.crop_options || [];
  const currentIndex = parseInt(location.pathname.split('/').pop());
  const configItem = productConfig[currentIndex];
  const product = configItem?.product;
  const posesCount = product?.poses_count || 1;

  // Same rule the routing used to get here (getNextUrl's completeSlot), so a
  // slot that should never have been routed here leaves again rather than
  // rendering a crop UI. The old guard tested `posesCount === 0`, which
  // `|| 1` above made unreachable for a product with no poses_count at all.
  const showCropOptions = productOffersCropOptions(product, shoot);

  const [cropSelections, setCropSelections] = useState([]);
  const [skipTriggered, setSkipTriggered] = useState(false);

  // Auto-skip anything this page cannot collect from: no product yet, or a
  // product the shared rule says has no crops to offer.
  useEffect(() => {
    if (!skipTriggered && (!product || !showCropOptions)) {
      setSkipTriggered(true);
      addToConfig({
        dispatch,
        history,
        productConfig,
        pages,
        itemToAdd: [],
        event: shoot,
      });
    }
  }, [
    product,
    skipTriggered,
    showCropOptions,
    shoot,
    additionalData,
    productConfig,
    dispatch,
    history,
  ]);

  if (!product || !showCropOptions) {
    return null;
  }

  const allSelected = cropSelections.filter(Boolean).length >= posesCount;

  const handleContinue = () => {
    addToConfig({
      dispatch,
      history,
      productConfig,
      pages,
      itemToAdd: cropSelections.filter(Boolean),
      event: shoot,
    });
  };

  return (
    <CropOptionsWrapper>
      <CropOptionsPrepaySelector
        cropOptions={cropOptions}
        posesCount={posesCount}
        cropSelections={cropSelections}
        onCropSelectionsChange={setCropSelections}
      />
      <ButtonWrapper>
        <ActionButton onClick={handleContinue} disabled={!allSelected}>
          Continue
        </ActionButton>
      </ButtonWrapper>
    </CropOptionsWrapper>
  );
};

export default withRouter(CropOptions);
